import { useState } from "react";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Edit, Building2, Tag, Globe, CreditCard, Lock } from "lucide-react";
import { toast } from "react-toastify";
import { Supplier } from "../../types/supplier";
import { validatePAN, validateCIN } from "../../utils/Validation";
import apiService from "../../services/apiService";

interface EditBasicInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  supplier: Supplier;
  onUpdate: (updatedSupplier: Supplier) => void;
}

interface ApiResponse<T> {
  data: T;
  message?: string;
  status?: number;
}

const inputCls =
  "w-full h-10 pl-9 pr-3 rounded-[8px] border border-[#dde3e4] bg-white text-[13px] font-medium text-[#151b1d] placeholder:text-[#7c8b91] focus:border-[#3f8b98] focus:ring-1 focus:ring-[#3f8b98] outline-none transition-all";
const lockedInputCls =
  "w-full h-10 pl-9 pr-8 rounded-[8px] border border-[#dde3e4] bg-[#f6f8f8] text-[13px] font-medium text-[#526066] cursor-not-allowed outline-none";
const labelCls = "block text-[12.5px] font-semibold text-[#151b1d] mb-1";
const iconCls = "absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7c8b91]";

export function EditBasicInfoDialog({
  open,
  onOpenChange,
  supplier,
  onUpdate,
}: EditBasicInfoDialogProps) {
  const [formData, setFormData] = useState({
    company_name: supplier.company_name,
    trade_name: supplier.trade_name || "",
    website_url: supplier.website_url || "",
    pan: supplier.pan,
    cin: supplier.cin || "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.company_name) {
      toast.error("Company Name is required");
      return;
    }
    if (formData.pan && !validatePAN(formData.pan)) {
      toast.error("Invalid PAN format (e.g. AABCT1234D)");
      return;
    }
    if (formData.cin && !validateCIN(formData.cin)) {
      toast.error("Invalid CIN format");
      return;
    }

    setSubmitting(true);
    try {
      const response: any = await apiService.post<ApiResponse<any>>(`/supplier/update-supplier`, {
        id: supplier.id,
        trade_name: formData.trade_name,
        website_url: formData.website_url,
        pan: formData.pan,
      });
      if (response.status === 200) {
        toast.success("Supplier information updated successfully.");
      }
    } catch {
      toast.error("Update failed. Please try again.");
    } finally {
      setSubmitting(false);
    }

    const updatedSupplier: Supplier = {
      ...supplier,
      ...formData,
      lastUpdated: new Date().toISOString().split("T")[0],
    };
    onUpdate(updatedSupplier);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[520px] w-[95vw] rounded-[16px] border border-[#dde3e4] bg-white p-0 shadow-2xl font-['Poppins',sans-serif] text-[#151b1d]">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-[#eaeeef]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-[10px] bg-[#e1f0f1] flex items-center justify-center flex-shrink-0">
              <Edit style={{ width: 17, height: 17 }} className="text-[#3f8b98]" />
            </div>
            <div>
              <h2 className="text-[16px] font-semibold text-[#151b1d]">Edit Basic Information</h2>
              <p className="text-[12px] text-[#526066] mt-0.5">
                Company name and PAN are verified and can't be changed
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Row 1: Company Name (locked) + Trade Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>
                Company Name <span className="text-[#b0453f]">*</span>{" "}
                <span className="text-[11px] font-normal text-[#7c8b91] normal-case">Locked</span>
              </label>
              <div className="relative">
                <Building2 className={iconCls} />
                <input
                  type="text"
                  value={formData.company_name}
                  readOnly
                  className={lockedInputCls}
                />
                <Lock className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#7c8b91]" />
              </div>
            </div>
            <div>
              <label className={labelCls}>Trade Name</label>
              <div className="relative">
                <Tag className={iconCls} />
                <input
                  type="text"
                  placeholder="e.g. vctechnosolutions"
                  value={formData.trade_name}
                  onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })}
                  className={inputCls}
                />
              </div>
            </div>
          </div>

          {/* Row 2: Website + PAN (locked) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Website URL</label>
              <div className="relative">
                <Globe className={iconCls} />
                <input
                  type="url"
                  placeholder="https://company.com"
                  value={formData.website_url}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  className={inputCls}
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>
                PAN Number{" "}
                <span className="text-[11px] font-normal text-[#7c8b91] normal-case">Locked</span>
              </label>
              <div className="relative">
                <CreditCard className={iconCls} />
                <input
                  type="text"
                  value={formData.pan}
                  readOnly
                  className={lockedInputCls}
                />
                <Lock className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#7c8b91]" />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2.5 pt-2 border-t border-[#eaeeef]">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="h-9 px-4 rounded-[8px] border border-[#dde3e4] bg-white text-[#151b1d] hover:border-[#3f8b98] text-[13px] font-medium transition-colors cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="h-9 px-5 rounded-[8px] bg-[#3f8b98] hover:bg-[#2e6b76] text-white text-[13px] font-semibold flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
            >
              {submitting ? "Saving…" : "✓ Update Information"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
